// ============================================================
// File: src/components/Users.js
// ✅ Version — View + Pagination Restored 
// (2025-12-03)
// ============================================================

import React, { useEffect, useState, useContext } from "react";
import Select from "react-select";
import { AuthContext } from "../context/AuthContext";
import {
  getUsers,
   getUserById,
  deleteUser,
  getActiveTenants,
  getLocationsByTenant,
  getDepartmentsByTenant,
} from "../services/userService";
import UserForm from "./UserForm";
import "./Users.css";
import ViewUser from "./ViewUser"; // ✅ Added 2025-11-03 — For view modal
import UserEdit from "./UserEdit"; // ✅ Added 2025-11-04 — for editing existing users

export default function Users() {
  // 🧩 Added on 2025-11-04 @ 00:43 IST
const [selectedUser, setSelectedUser] = useState(null);
const [showViewModal, setShowViewModal] = useState(false);
//const [showEditDrawer, setShowEditDrawer] = useState(false);

  const { user } = useContext(AuthContext);
  const roleCode = (user?.role_code || "").toUpperCase();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const [search, setSearch] = useState("");

  const [tenants, setTenants] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  // ============================================================
  // 🎨 Select Styling (gradient tags)
  // ============================================================
  const selectStyles = {
    container: (base) => ({ ...base, minWidth: 200, zIndex: 10 }),
    multiValue: (base) => ({
      ...base,
      background: "linear-gradient(90deg,#007bff,#6f42c1)",
      color: "white",
      borderRadius: 12,
      paddingInline: 4,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      fontWeight: "600",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      ":hover": { background: "#dc3545", color: "white" },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // ============================================================
  // 🔹 Load Filters
  // ============================================================
  useEffect(() => {
    const loadFilters = async () => {
      try {
        if (roleCode === "SUPER_ADMIN") {
          const tenantData = await getActiveTenants();
          const tenantOptions = (tenantData || []).map((t) => ({
            value: t.id,
            label: t.name,
          }));
          setTenants(tenantOptions);
        } else if (user?.tenant_id) {
          setTenants([{ value: user.tenant_id, label: user.tenant_name }]);
          setSelectedTenant({
            value: user.tenant_id,
            label: user.tenant_name,
          });
        }
      } catch (err) {
        console.error("❌ Error loading filters:", err);
      }
    };
    loadFilters();
  }, [roleCode, user]);

  useEffect(() => {
    const loadLocationDept = async () => {
      try {
        if (!selectedTenant) return;
        const [locData, deptData] = await Promise.all([
          getLocationsByTenant(selectedTenant.value),
          getDepartmentsByTenant(selectedTenant.value),
        ]);
        setLocations(locData.map((l) => ({ value: l.id, label: l.name })));
        setDepartments(deptData.map((d) => ({ value: d.id, label: d.name })));
      } catch (err) {
        console.error("❌ Error loading tenant details:", err);
      }
    };
    loadLocationDept();
  }, [selectedTenant]);

  // ============================================================
  // 🔹 Load Users
  // ============================================================
  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await getUsers();
      setUsers(res || []);
    } catch (err) {
      console.error("❌ Error fetching users:", err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // ============================================================
  // 🔹 Actions
  // ============================================================
  const handleAdd = () => {
    setEditUser(null);
  setShowAddDrawer(true);
  };
const handleEdit = async (userId) => {
  try {
    const data = await getUserById(userId);
    console.log("✏️ EditUser data:", data);
    if (!data) return alert("User details not found!");
    //setSelectedUser(data);
    //setShowEditDrawer(true);
    setEditUser(data);
    setShowEditDrawer(true);

  } catch (err) {
    console.error("❌ Edit user failed:", err);
    alert("Failed to fetch user details.");
  }
};

  const handleView = async (userId) => {
  try {
     const data = await getUserById(userId);
    console.log("👁 ViewUser data:", data);
    if (!data) return alert("User details not found!");
    setSelectedUser(data);
    setShowViewModal(true);
  } catch (err) {
    console.error("❌ View user failed:", err);
    alert("Failed to fetch user details.");
  }
};

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await deleteUser(id);
      loadUsers();
    } catch (err) {
      console.error("❌ Error deleting user:", err);
    }
  };

  // ============================================================
  // 🔹 Filter + Pagination
  // ============================================================
  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role_name?.toLowerCase().includes(q)
    );
  });

  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRows = filteredUsers.slice(startIndex, startIndex + rowsPerPage);
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  // ============================================================
  // 🧭 UI
  // ============================================================
  return (
    <div className="container-fluid">
      {/* Header */}
      <div
        className="users-hero shadow-sm d-flex justify-content-between align-items-center px-4 flex-wrap"
        style={{
          background: "linear-gradient(90deg,#007bff,#6f42c1)",
          color: "white",
        }}
      >
        <div>
          <div className="h4 fw-bold mb-1 text-white">User Management</div>
          <div className="text-white-50 small">
            Manage all system users and their roles, locations, and departments.
          </div>
        </div>
        <button
  onClick={handleAdd}
  className="btn d-flex align-items-center"
  style={{
    background: "linear-gradient(90deg, #007bff, #6f42c1)",
    border: "none",
    color: "white",
    fontWeight: 600,
    padding: "10px 20px",
    borderRadius: "8px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    transition: "all 0.3s ease",
  }}
  onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
  onMouseLeave={(e) => (e.target.style.opacity = "1")}
>
  <i className="fa fa-plus me-2"></i> Add User
</button>
      </div>

      {/* Filters */}
      <div className="users-filter-toolbar d-flex flex-wrap align-items-center gap-2 mt-2">
        <Select
          options={tenants}
          value={selectedTenant}
          onChange={setSelectedTenant}
          placeholder="Select Tenant..."
          styles={selectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
        <Select
          options={locations}
          value={selectedLocation}
          onChange={setSelectedLocation}
          placeholder="Select Location..."
          styles={selectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
        <Select
          options={departments}
          value={selectedDepartment}
          onChange={setSelectedDepartment}
          placeholder="Select Department..."
          styles={selectStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: 250 }}
          placeholder="Search user name / email / role..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="card shadow-sm mt-3 table-responsive">
        {loading ? (
          <div className="text-center p-4">Loading users...</div>
        ) : currentRows.length === 0 ? (
          <div className="text-center p-4 text-muted">No users found.</div>
        ) : (
          <table className="table table-sm users-table mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Tenant</th>
                <th>Location</th>
                <th>Department</th>
                <th>Role</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{u.tenant_name}</td>
                  <td>{u.location_name}</td>
                  <td>{u.department_name}</td>
                  <td>{u.role_name}</td>
                  <td>
                    <span
                      className={`badge ${
                        u.status === "Active" ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td className="text-center">
                    {/* 👁️ View icon restored */}
                    <i
                      className="fa fa-eye text-primary mx-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleView(u.id)}
                    ></i>
                    <i
                      className="fa fa-edit text-success mx-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleEdit(u.id)}
                    ></i>
                    <i
                      className="fa fa-trash text-danger mx-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleDelete(u.id)}
                    ></i>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ✅ Pagination Restored */}
      {!loading && currentRows.length > 0 && (
        <div className="d-flex justify-content-between align-items-center p-2 flex-wrap">
          <div className="small text-muted">
            Showing {startIndex + 1} to{" "}
            {Math.min(startIndex + rowsPerPage, filteredUsers.length)} of{" "}
            {filteredUsers.length} entries
          </div>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setCurrentPage(1)}>
                «
              </button>
            </li>
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                ‹
              </button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li
                key={i}
                className={`page-item ${
                  currentPage === i + 1 ? "active" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                ›
              </button>
            </li>
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => setCurrentPage(totalPages)}
              >
                »
              </button>
            </li>
          </ul>
        </div>
      )}

     {/* ✅ Updated 2025-11-04 — Use UserEdit for editing */}
{/* ✅ Add User Drawer */}
{showAddDrawer && (
  <UserForm
    show={showAddDrawer}
    onClose={() => {
      setShowAddDrawer(false);
      loadUsers();
    }}
    editData={null}
  />
)}

{/* ✅ Edit User Drawer */}
{showEditDrawer && editUser && (
  <UserEdit
    userData={editUser}
    onClose={() => {
      setShowEditDrawer(false);
      setEditUser(null);
    }}
    onSuccess={() => {
      setShowEditDrawer(false);
      setEditUser(null);
      loadUsers();
    }}
  />
)}

      
{showViewModal && selectedUser && (
  <ViewUser
    userData={selectedUser}        // 🔹 Correct prop name
    onClose={() => setShowViewModal(false)}
    onEdit={() => handleEdit(selectedUser.id)}  // 🔹 Optional: allows “Edit User” button to open drawer
  />
)}
    </div>
  );
}
